import {
  codesWithVisits,
  propertyEntry,
  resolvePropertyCode,
  visitsForProperty,
  type PropertyCatalog,
  type PropertyVisit,
} from './propertyCatalog';

export type CalendarRange = 'week' | 'month' | 'fourMonth';
export type CalendarTripSource = 'catalog' | 'live' | 'planned';

export interface CalendarTrip {
  id: string;
  title: string;
  start: string;
  end: string;
  source: CalendarTripSource;
  status?: string | null;
  locationName?: string | null;
  notes?: string | null;
  propertyCode?: string;
  tone?: 'current' | 'upcoming' | 'past';
}

export interface LiveTripLike {
  id: string;
  project_number: string;
  scope: string | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  location?: { id?: string; name?: string } | { id?: string; name?: string }[] | null;
}

const MONTHS: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

export function parseISODate(iso: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return new Date(NaN);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function toISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function todayISO(now = new Date()): string {
  return toISODate(now);
}

function isoFromParts(year: number, monthIndex: number, day: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function monthIndex(name: string): number | null {
  const index = MONTHS[name.toLowerCase()];
  return index === undefined ? null : index;
}

/** Map a curated window like "Jul 13–17, 2026" onto inclusive ISO dates. */
export function parseTripRange(visit: Pick<PropertyVisit, 'windowLabel' | 'occurredOn'>): {
  start: string;
  end: string;
} {
  const window = visit.windowLabel.trim();

  const cross = /^([A-Za-z]+)\s+(\d{1,2})\s*[–-]\s*([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/.exec(window);
  if (cross) {
    const startMonth = monthIndex(cross[1]);
    const endMonth = monthIndex(cross[3]);
    const year = Number(cross[5]);
    if (startMonth !== null && endMonth !== null) {
      return {
        start: isoFromParts(year, startMonth, Number(cross[2])),
        end: isoFromParts(year, endMonth, Number(cross[4])),
      };
    }
  }

  const same = /^([A-Za-z]+)\s+(\d{1,2})\s*[–-]\s*(\d{1,2}),\s*(\d{4})$/.exec(window);
  if (same) {
    const month = monthIndex(same[1]);
    const year = Number(same[4]);
    if (month !== null) {
      return {
        start: isoFromParts(year, month, Number(same[2])),
        end: isoFromParts(year, month, Number(same[3])),
      };
    }
  }

  const single = /^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/.exec(window);
  if (single) {
    const month = monthIndex(single[1]);
    if (month !== null) {
      const iso = isoFromParts(Number(single[3]), month, Number(single[2]));
      return { start: iso, end: iso };
    }
  }

  return { start: visit.occurredOn, end: visit.occurredOn };
}

export function catalogToCalendarTrips(visits: PropertyVisit[] | null | undefined): CalendarTrip[] {
  if (!visits) return [];
  const today = todayISO();
  return visits.map((visit) => {
    const range = parseTripRange(visit);
    return {
      id: `catalog:${visit.id}`,
      title: visit.title,
      start: range.start,
      end: range.end,
      source: 'catalog',
      status: range.end < today ? 'completed' : 'scheduled',
    };
  });
}

function locationName(
  location: LiveTripLike['location'],
): string | null {
  if (!location) return null;
  if (Array.isArray(location)) return location[0]?.name ?? null;
  return location.name ?? null;
}

export function liveToCalendarTrips(trips: LiveTripLike[] | null | undefined): CalendarTrip[] {
  if (!trips) return [];
  return trips.flatMap((trip) => {
    const start = trip.start_date;
    if (!start) return [];
    return [{
      id: `live:${trip.id}`,
      title: trip.scope?.trim() || trip.project_number,
      start,
      end: trip.end_date ?? start,
      source: 'live' as const,
      status: trip.status,
      locationName: locationName(trip.location),
    }];
  });
}

export function mergeCalendarTrips(...groups: CalendarTrip[][]): CalendarTrip[] {
  const seen = new Set<string>();
  const merged: CalendarTrip[] = [];
  for (const group of groups) {
    for (const trip of group) {
      const key = `${trip.start}|${trip.end}|${trip.title.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(trip);
    }
  }
  return merged.sort((a, b) => a.start.localeCompare(b.start) || a.title.localeCompare(b.title));
}

export function tripCoversDate(trip: CalendarTrip, iso: string): boolean {
  return iso >= trip.start && iso <= trip.end;
}

export function tripsOnDate(trips: CalendarTrip[], iso: string): CalendarTrip[] {
  return trips.filter((trip) => tripCoversDate(trip, iso));
}

export function startOfWeek(date: Date): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() - next.getDay());
  return next;
}

export function weekCells(anchor: Date): Date[] {
  const start = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, index) => (
    new Date(start.getFullYear(), start.getMonth(), start.getDate() + index)
  ));
}

export function monthCells(year: number, month: number): Date[] {
  const start = startOfWeek(new Date(year, month, 1));
  return Array.from({ length: 42 }, (_, index) => (
    new Date(start.getFullYear(), start.getMonth(), start.getDate() + index)
  ));
}

export function fourMonthStarts(anchor: Date): Date[] {
  const base = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  return [0, 1, 2, 3].map((offset) => new Date(base.getFullYear(), base.getMonth() + offset, 1));
}

export function shiftAnchor(anchor: Date, view: CalendarRange, direction: -1 | 1): Date {
  if (view === 'week') {
    return new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() + direction * 7);
  }
  if (view === 'month') {
    return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1);
  }
  return new Date(anchor.getFullYear(), anchor.getMonth() + direction * 4, 1);
}

export function rangeLabel(anchor: Date, view: CalendarRange): string {
  if (view === 'week') {
    const days = weekCells(anchor);
    const start = days[0];
    const end = days[6];
    const startLabel = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endLabel = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startLabel} – ${endLabel}`;
  }
  if (view === 'month') {
    return anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  const months = fourMonthStarts(anchor);
  const start = months[0];
  const end = months[3];
  return `${start.toLocaleDateString('en-US', { month: 'short' })} – ${end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
}

export function visibleRange(anchor: Date, view: CalendarRange): { start: string; end: string } {
  if (view === 'week') {
    const days = weekCells(anchor);
    return { start: toISODate(days[0]), end: toISODate(days[6]) };
  }
  if (view === 'month') {
    const cells = monthCells(anchor.getFullYear(), anchor.getMonth());
    return { start: toISODate(cells[0]), end: toISODate(cells[41]) };
  }
  const months = fourMonthStarts(anchor);
  const last = months[3];
  const lastDay = new Date(last.getFullYear(), last.getMonth() + 1, 0);
  return { start: toISODate(months[0]), end: toISODate(lastDay) };
}

export function tripsInRange(trips: CalendarTrip[], start: string, end: string): CalendarTrip[] {
  return trips.filter((trip) => trip.start <= end && trip.end >= start);
}

/** Prefer the next visit; otherwise the most recent one so history isn't off-screen. */
export function initialAnchor(trips: CalendarTrip[], now = new Date()): Date {
  const today = todayISO(now);
  const upcoming = trips
    .filter((trip) => trip.end >= today)
    .sort((a, b) => a.start.localeCompare(b.start));
  if (upcoming[0]) return parseISODate(upcoming[0].start);
  const latest = [...trips].sort((a, b) => b.end.localeCompare(a.end) || b.start.localeCompare(a.start))[0];
  if (latest) return parseISODate(latest.end);
  return now;
}

export function formatTripWindow(start: string, end: string): string {
  const startDate = parseISODate(start);
  const endDate = parseISODate(end);
  if (start === end) {
    return startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${endDate.getDate()}, ${endDate.getFullYear()}`;
  }
  const startLabel = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endLabel = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startLabel} – ${endLabel}`;
}

export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export interface PropertySwatch {
  fill: string;
  text: string;
  swatch: string;
}

/** Stable hues so a Director of IT can scan coverage by property. */
export const PROPERTY_CALENDAR_COLORS: Record<string, PropertySwatch> = {
  HCC: { fill: 'bg-sky-500/20', text: 'text-sky-800 dark:text-sky-200', swatch: 'bg-sky-500' },
  HDC: { fill: 'bg-violet-500/20', text: 'text-violet-800 dark:text-violet-200', swatch: 'bg-violet-500' },
  PHX: { fill: 'bg-amber-500/20', text: 'text-amber-900 dark:text-amber-200', swatch: 'bg-amber-500' },
  MSD: { fill: 'bg-emerald-500/20', text: 'text-emerald-800 dark:text-emerald-200', swatch: 'bg-emerald-500' },
  SAQ: { fill: 'bg-rose-500/20', text: 'text-rose-800 dark:text-rose-200', swatch: 'bg-rose-500' },
  SHQ: { fill: 'bg-slate-500/20', text: 'text-slate-800 dark:text-slate-200', swatch: 'bg-slate-500' },
  LSF: { fill: 'bg-fuchsia-500/20', text: 'text-fuchsia-800 dark:text-fuchsia-200', swatch: 'bg-fuchsia-500' },
  LNY: { fill: 'bg-indigo-500/20', text: 'text-indigo-800 dark:text-indigo-200', swatch: 'bg-indigo-500' },
  MRC: { fill: 'bg-teal-500/20', text: 'text-teal-800 dark:text-teal-200', swatch: 'bg-teal-500' },
  HPK: { fill: 'bg-orange-500/20', text: 'text-orange-900 dark:text-orange-200', swatch: 'bg-orange-500' },
  HWD: { fill: 'bg-cyan-500/20', text: 'text-cyan-800 dark:text-cyan-200', swatch: 'bg-cyan-500' },
  HBG: { fill: 'bg-lime-500/20', text: 'text-lime-900 dark:text-lime-200', swatch: 'bg-lime-500' },
  HSC: { fill: 'bg-blue-500/20', text: 'text-blue-800 dark:text-blue-200', swatch: 'bg-blue-500' },
};

const FALLBACK_SWATCH: PropertySwatch = {
  fill: 'bg-primary/15',
  text: 'text-primary',
  swatch: 'bg-primary',
};

/**
 * Calendar swatch for a property. Takes a canonical registry code — callers
 * resolve aliases through the catalog before they get here — so this stays a
 * pure presentation lookup with no data dependency.
 */
export function propertyCalendarColor(code: string | null | undefined): PropertySwatch {
  const key = code?.trim().toUpperCase();
  if (!key) return FALLBACK_SWATCH;
  return PROPERTY_CALENDAR_COLORS[key] ?? FALLBACK_SWATCH;
}

export function coverageTone(trip: Pick<CalendarTrip, 'start' | 'end'>, now = new Date()): NonNullable<CalendarTrip['tone']> {
  const today = todayISO(now);
  if (trip.end < today) return 'past';
  if (trip.start <= today) return 'current';
  return 'upcoming';
}

export function withCoverageTones(trips: CalendarTrip[], now = new Date()): CalendarTrip[] {
  return trips
    .map((trip) => {
      const tone = coverageTone(trip, now);
      return {
        ...trip,
        tone,
        status: tone === 'past' ? trip.status ?? 'completed' : tone === 'current' ? 'in_progress' : trip.status ?? 'scheduled',
      };
    })
    .sort((a, b) => a.start.localeCompare(b.start) || a.title.localeCompare(b.title));
}

export function currentAndFutureTrips(trips: CalendarTrip[], now = new Date()): CalendarTrip[] {
  return withCoverageTones(trips, now).filter((trip) => trip.tone !== 'past');
}

export function portfolioLegendProperties(
  catalog: PropertyCatalog,
): { code: string; name: string; count: number }[] {
  return codesWithVisits(catalog).map((code) => ({
    code,
    name: propertyEntry(catalog, code)?.name ?? code,
    count: visitsForProperty(catalog, code).length,
  }));
}

export function portfolioCoverageTrips(
  catalog: PropertyCatalog,
  now = new Date(),
  propertyCode?: string | null,
): CalendarTrip[] {
  const today = todayISO(now);
  const wanted = propertyCode ? resolvePropertyCode(catalog, propertyCode) : null;
  const trips: CalendarTrip[] = [];
  for (const code of codesWithVisits(catalog)) {
    if (wanted && code !== wanted) continue;
    const name = propertyEntry(catalog, code)?.name ?? code;
    for (const trip of catalogToCalendarTrips(visitsForProperty(catalog, code))) {
      // Portfolio view is forward-looking unless one property is selected.
      if (!wanted && trip.end < today) continue;
      const tone = coverageTone(trip, now);
      trips.push({
        ...trip,
        id: `portfolio:${code}:${trip.id}`,
        propertyCode: code,
        locationName: name,
        tone,
        status: tone === 'past' ? 'completed' : tone === 'current' ? 'in_progress' : 'scheduled',
      });
    }
  }
  return trips.sort((a, b) => a.start.localeCompare(b.start) || (a.propertyCode ?? '').localeCompare(b.propertyCode ?? ''));
}

export function withPropertyCode(
  catalog: PropertyCatalog,
  trip: CalendarTrip,
  code: string | null | undefined,
  name?: string | null,
): CalendarTrip {
  const resolved = resolvePropertyCode(catalog, code) ?? undefined;
  const entry = propertyEntry(catalog, resolved);
  return {
    ...trip,
    propertyCode: resolved,
    locationName: name ?? entry?.name ?? trip.locationName ?? resolved ?? trip.locationName,
  };
}
